import { Box } from "@mui/material"
import React, { useCallback, useEffect, useId, useRef, useState } from "react"
import { s } from "src/utilities/common"

import { GradientOverlay } from "../../components/common/GradientOverlay/GradientOverlay"
import { PaginationButton } from "../../components/common/PaginationButton"
import { useEventBind } from "../../hooks/useEventBind"
import { appStore } from "../../store/AppStore"
import { Buttons, keydown } from "../../utilities/constants"

import { Slides } from "./Slides/Slides"

export type SlideIndex = 1 | 2

export const OnboardingScreen = () => {
  const slides = [1, 2] as const
  const [slideIndex, setSlideIndex] = useState<SlideIndex>(1)
  const id = useId()
  const setSpinner = appStore((state) => state.setSpinner)
  const registerBackButtonOverride = appStore(
    (state) => state.registerBackButtonOverride,
  )

  const [ownBBOverride, setOwnBBOverride] = useState(false)

  useEffect(() => {
    registerBackButtonOverride(id, slideIndex > 1)
  }, [slideIndex])

  const keyListener = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === Buttons.Back && slideIndex !== 1 && !ownBBOverride)
        setTimeout(() => setSlideIndex(slides[slideIndex - 2]), 50)
    },
    [slideIndex, ownBBOverride],
  )

  const [loaded, setLoaded] = useState(false)
  const loadedHelper = useRef(loaded)

  useEffect(() => {
    loadedHelper.current = loaded
    if (loaded) setSpinner("out")
    // show screen even if video isn't playing yet
    const timer = setTimeout(() => {
      setLoaded(true)
      setSpinner("out")
    }, 3000)
    return () => clearTimeout(timer)
  }, [loaded])

  useEventBind(window, keydown, keyListener)
  useEffect(() => {
    // wait for element to be created in dom (window.onload isn't working on webOS)
    const checkFaderInterval = setInterval(() => {
      if (document.getElementById("fader") !== null) {
        if (!loadedHelper.current) setSpinner("in")
        clearInterval(checkFaderInterval)
      }
    }, 100)
    return () => {
      registerBackButtonOverride(id, false)
    }
  }, [])

  return (
    <Box
      sx={{
        visibility: loaded ? undefined : "hidden",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 1,
          height: "100%",
          width: "100%",
        }}
      >
        <GradientOverlay type={"full"} />
        <video
          aria-hidden={true}
          onCanPlay={() => setLoaded(true)}
          id={"onboardingvideo"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "fill",
          }}
          src={
            "https://cdn.staging.stage-plus.com/apps/v1/onboarding/loop-16_9.mp4"
          }
          autoPlay={true}
          loop={true}
          muted={true}
        />
      </Box>
      {loaded && (
        <Slides
          nextSlideAction={setSlideIndex}
          index={slideIndex}
          setBBOverride={setOwnBBOverride}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          mt: s("66px"),
          mb: s("88.5px"),
          justifyContent: "center",
          zIndex: 3,
        }}
      >
        {slides.map((index) => (
          <PaginationButton
            key={"pagination:" + index}
            disabled
            startIndex={1}
            index={index}
            onMouseDown={() => setSlideIndex(index)}
            selected={index === slideIndex}
          />
        ))}
      </Box>
    </Box>
  )
}
